import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/class-control";

export interface NotificationPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export function NotificationPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}: NotificationPaginationProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers (show max 4 pages)
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPages = 4;

    if (totalPages <= maxPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxPages; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxPages + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 2 && i <= totalPages; i++) {
          pages.push(i);
        }
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex gap-[962px] items-center w-full">
      <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#888888] text-[14px] whitespace-pre">
        Exibindo {startItem} de {totalItems} notificações
      </p>

      <div className="flex gap-[13px] items-center">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={cn(
            "flex items-center justify-center size-[40px] rounded-[20px] transition-colors",
            currentPage === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-white/10 cursor-pointer"
          )}
          aria-label="Página anterior"
        >
          <div className="rotate-180">
            <ChevronRight className="size-[18px] text-white" />
          </div>
        </button>

        {/* Page Numbers */}
        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={cn(
              "flex items-center justify-center size-[40px] rounded-[20px]",
              "font-['Plus_Jakarta_Sans'] font-normal text-[14px] text-center text-white",
              "transition-colors",
              page === currentPage
                ? "bg-[#7846FF]"
                : "hover:bg-white/10"
            )}
            aria-label={`Página ${page}`}
            aria-current={page === currentPage ? "page" : undefined}
          >
            {page}
          </button>
        ))}

        {/* Next Button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={cn(
            "flex items-center justify-center size-[40px] rounded-[20px] transition-colors",
            currentPage === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-white/10 cursor-pointer"
          )}
          aria-label="Próxima página"
        >
          <ChevronRight className="size-[18px] text-white" />
        </button>
      </div>
    </div>
  );
}
